import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Divider, styled } from '@mui/material';
import { useDirectQualificationsStore, useOrgQualificationsStore } from '@libs/store/settings';
import { useOrgDepartmentsStore } from '@libs/store/settings/departments';
import { useShiftDrawerTalents } from '@libs/store/shifts';
import { useLocationStore } from '@libs/store/locations';
import { SingleShift } from '@libs/models/shifts/shift';
import { Form, FormBlock, FormRadioGroup, FormTextField } from '@molecules/form';
import { Flex, Grid } from '@atoms/layout';
import { Button } from '@atoms/buttons';
import { QualificationSelect } from './qualificationSelect';
import { AssigneesSelect } from './assigneesSelect';
import { LocationFormData, LocationSelectionSection } from '@pages/schedule/components';

type EditSeriesFormProps = {
  loading?: boolean;
  onSave: (shift: ShiftSeriesFormData) => Promise<void>;
  data: ShiftSeriesFormData | null;
  localizationSubset: string;
  onFormStateChanged?: (state: { isDirty: boolean }) => void;
};
export function EditSeriesForm({
  data,
  onSave,
  loading: dataLoading,
  localizationSubset,
  onFormStateChanged,
}: EditSeriesFormProps) {
  const intl = useIntl();
  const currentLocationID = useLocationStore((s) => s.currentLocationID);
  const {
    load: loadDirectQualifications,
    loading: isDirectQualListLoading,
    data: directQualData,
  } = useDirectQualificationsStore();
  const orgQualifications = useOrgQualificationsStore((s) => s.orgQualifications);
  const loadOrgQuals = useOrgQualificationsStore((s) => s.load);
  const isOrgQualListLoading = useOrgQualificationsStore((s) => s.loading);
  const { departmentsTreeLoading } = useOrgDepartmentsStore();
  const loadProviders = useShiftDrawerTalents((s) => s.load);
  const talents = useShiftDrawerTalents((s) => s.talents);
  const talentsLoading = useShiftDrawerTalents((s) => s.loading);
  const disabled = !!data && data.assignees.length > 0;

  const loading =
    dataLoading || isOrgQualListLoading || talentsLoading || departmentsTreeLoading || isDirectQualListLoading;

  useEffect(() => {
    if (!orgQualifications) {
      loadOrgQuals();
    }
  }, [loadOrgQuals, orgQualifications]);

  useEffect(() => {
    if (!directQualData || !directQualData.length) {
      loadDirectQualifications();
    }
  }, [loadDirectQualifications, directQualData]);

  const methods = useForm<ShiftSeriesFormData>({
    defaultValues: {
      location: currentLocationID || '',
      department: '',
      subDepartment: '',
      specialty: '',

      staffingType: 'All',
      qualifications: [],
      assignees: [],

      tags: '',
    },
  });
  const {
    setValue,
    getValues,
    watch,
    reset,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    onFormStateChanged?.({ isDirty });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  useEffect(() => {
    if (!data) {
      return;
    }
    reset({
      ...getValues(),
      ...data,
    });
  }, [data, getValues, reset]);

  const assigneesIds = watch('assignees');
  const assigned = talents.filter((talent) => assigneesIds.includes(talent.id));
  const locationId = watch('location');

  useEffect(() => {
    setValue('location', currentLocationID || '');
  }, [currentLocationID, setValue]);

  useEffect(() => {
    const filters = [];
    filters.push({ key: 'locations', value: [locationId] });
    // TODO: use autocomplete
    loadProviders({
      size: 50,
      filters: {
        locations: {
          key: 'locations',
          value: [locationId],
        },
      },
    });
  }, [loadProviders, locationId]);

  return (
    <Form formUtils={methods} style={{ height: '100%' }} onSubmit={onSave}>
      <Flex column height="100%">
        <Flex column mx={2} py={2} height="100%" overflow="auto">
          {/* Location */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.location.sectTitle`} />}>
            <LocationSelectionSection localizationSubset={localizationSubset} loading={loading} disabled={disabled} />
          </FormBlock>

          {/* Talent */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.talentConfiguration.sectTitle`} />}>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <FormRadioGroup
                  name="staffingType"
                  label={<FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.label`} />}
                  items={[
                    {
                      label: <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.all`} />,
                      value: 'All',
                    },
                    {
                      label: (
                        <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.internal`} />
                      ),
                      value: 'Internal',
                    },
                    {
                      label: (
                        <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.external`} />
                      ),
                      value: 'External',
                    },
                  ]}
                  required
                  disabled={loading || disabled}
                  row
                />
              </Grid>
              <Grid xs={12}>
                <QualificationSelect
                  fullWidth
                  name="qualifications"
                  required
                  localizationSubset={localizationSubset}
                  disabledOptions={assigned.flatMap((a) => a.qualifications.map((q) => q.id))}
                  disabled={disabled}
                />
              </Grid>
              <Grid xs={12}>
                <AssigneesSelect
                  fullWidth
                  name="assignees"
                  // We oly select 1 assignee when editing
                  numberOfCopies={1}
                  localizationSubset={localizationSubset}
                  selectedQualifications={watch('qualifications')}
                  disabled={watch('qualifications').length === 0 || disabled}
                />
              </Grid>
            </Grid>
          </FormBlock>

          {/* Tags */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.additionalInfo.sectTitle`} />}>
            <Grid container spacing={2}>
              <Grid xs={6}>
                <FormTextField
                  disabled={loading || disabled}
                  label={<FormattedMessage id={`${localizationSubset}.additionalInfo.tag.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.additionalInfo.tag.placeholder`,
                  })}
                  fullWidth
                  name="tags"
                />
              </Grid>
            </Grid>
          </FormBlock>
        </Flex>

        {/* Controls */}
        <Flex
          column
          position="sticky"
          bottom={0}
          height={isDirty ? '70px' : '0'}
          overflow="hidden"
          sx={{ transition: 'height 0.3s' }}
        >
          <Divider />
          <Flex gap={2} justifyContent="right" p={2} bgcolor={'background.paper'}>
            <MenuButton variant="outlined" onClick={() => reset()} disabled={loading}>
              <FormattedMessage id="common.cancel" />
            </MenuButton>
            <MenuButton variant="contained" type="submit" disabled={loading}>
              <FormattedMessage id={`common.save`} />
            </MenuButton>
          </Flex>
        </Flex>
      </Flex>
    </Form>
  );
}

const MenuButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(16.75),
}));

export type ShiftSeriesFormData = LocationFormData & {
  id: string;
  staffingType: SingleShift['staffingType'];
  qualifications: string[];
  assignees: string[];
  tags: string;
};
